import { faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";

const SPEEDS = [1, 1.5, 2] as const;

interface IProps {
  src: string;
}

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${String(secs).padStart(2, "0")}`;
};

const CallRecordingPlayer: FC<IProps> = ({ src }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sourceRef = useRef<string | null>(null);
  const playingRef = useRef(false);
  const currentTimeRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);

  const updatePlaying = (value: boolean) => {
    playingRef.current = value;
    setPlaying(value);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => {
      currentTimeRef.current = audio.currentTime;
      setCurrentTime(audio.currentTime);
    };
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => updatePlaying(false);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const previousSrc = sourceRef.current;
    sourceRef.current = src;
    if (previousSrc === null) return;

    const shouldResume = playingRef.current;
    const resumeTime = currentTimeRef.current;
    const restorePlayback = () => {
      if (Number.isFinite(resumeTime)) {
        audio.currentTime = Math.min(
          resumeTime,
          Number.isFinite(audio.duration) ? audio.duration : resumeTime,
        );
      }
      if (shouldResume) {
        void audio.play().catch(() => updatePlaying(false));
      }
    };

    audio.addEventListener("loadedmetadata", restorePlayback, { once: true });
    if (audio.readyState >= 1) restorePlayback();

    return () => audio.removeEventListener("loadedmetadata", restorePlayback);
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      updatePlaying(false);
    } else {
      updatePlaying(true);
      void audio.play().catch(() => updatePlaying(false));
    }
  };

  const onSeek = (e: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    const time = Number(e.target.value);
    if (audio) audio.currentTime = time;
    currentTimeRef.current = time;
    setCurrentTime(time);
  };

  const cycleSpeed = () => {
    const nextIndex = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(nextIndex);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[nextIndex];
  };

  return (
    <div
      className="d-flex align-items-center gap-2"
      style={{ minWidth: 220 }}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        className="btn btn-sm btn-link p-0 text-body-secondary"
        onClick={togglePlay}
        aria-label={playing ? "Mettre en pause" : "Lire l'enregistrement"}
      >
        <FontAwesomeIcon icon={playing ? faPause : faPlay} fixedWidth />
      </button>
      <input
        type="range"
        className="form-range flex-grow-1"
        min={0}
        max={duration || 0}
        step={0.1}
        value={currentTime}
        onChange={onSeek}
        aria-label="Progression de la lecture"
      />
      <span className="fs--2 text-body-tertiary text-nowrap">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary py-0 px-1 fs--2"
        onClick={cycleSpeed}
        aria-label="Changer la vitesse de lecture"
      >
        {SPEEDS[speedIndex]}x
      </button>
    </div>
  );
};

export default CallRecordingPlayer;
